import React, { useCallback, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Link,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { has } from "lodash";
import Uppy from "@uppy/core";
import { useUppy } from "@uppy/react";
import {
  FaArrowRight,
  FaCheck,
  FaCloudDownloadAlt,
  FaDownload,
  FaPen,
  FaTrash,
  FaVideo,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getCurrentLocaleCode, getLocale } from "../../_utils/uppyLocale";
import UppyDropFile from "../social/UppyDropFile/UppyDropFile";
import * as ImportPostStatus from "../social/PostsBulkImport/ImportPostStatus.constants";

import useScraper from "../social/PostLinkPreview/useScraper";

import * as PostEditorModes from "../social/PostEditor/PostEditorModes.constants";
import * as PostEditorActions from "../social/PostEditor/PostEditorActions.constants";
import axios from "axios";
import { BULK_CREATE_ENDPOINT } from "apps/simplified-web/src/_actions/endpoints";
import { parseToArray } from "../../_utils/csv";
import { checkCeleryTaskStatus, getFeatureLimit } from "../../_utils/common";
import { FeatureCodes } from "../details/constants";
import { updatePostsBulkImportItem } from "../../_actions/plannerActions";
import { showToast } from "../../_utils/showToast";
import PostEditorViewModal from "../social/PostEditorView/PostEditorViewModal";
import { CSVLink } from "react-csv";
import { isEmpty } from "lodash";

const BulkArtboardCreation = ({ onProgress, designId }) => {
  const { t } = useTranslation(["planner", "common"]);
  const [isUploading, setIsUploading] = useState(false);
  const [preparingInProgress, setPreparingInProgress] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [items, setItems] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileData, setFileData] = useState({});
  const [itemEdit, setItemEdit] = useState(null);
  const [itemsLimit, setItemsLimit] = useState(0);
  const [loading, setLoading] = useState(false);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const { fetchScraperData } = useScraper();
  const { onOpen: handleOpenUpgradeModal } = useDisclosure();
  const dispatch = useDispatch();
  const subscriptionData = useSelector((state) => state.subscription);
  const userId = useSelector((state) => state.auth.payload?.user?.pk);
  const layers = useSelector((state) => state.layerstore.layers);
  const layesKeys = Object.keys(layers).map((ele) => ele);
  const newLayers = layesKeys.map((ele) => layers[ele]);
  const fileHeaders = newLayers.map((ele) => {
    return { label: ele.title, key: "" };
  });

  const workspace = useSelector((state) =>
    state.auth.payload?.orgs?.find(
      (item) => item.id === state.auth.payload?.selectedOrg
    )
  );
  const defaultTimezone = useMemo(
    () => workspace?.settings?.timezone,
    [workspace]
  );

  const maxFileSize = 1024 * 1024 * 5;
  const uppy = useUppy(
    () =>
      new Uppy({
        id: "posts-bulk-import",
        autoProceed: true,
        allowMultipleUploadBatches: false,
        restrictions: {
          maxFileSize,
          allowedFileTypes: [".csv"],
          maxNumberOfFiles: 1,
        },
        locale: getLocale(getCurrentLocaleCode()),
      })
  );

  useEffect(() => {
    setItemsLimit(
      getFeatureLimit(subscriptionData, FeatureCodes.PLANNER_BULK_IMPORT) || 100
    );
  }, [subscriptionData]);

  const handleResetSelectedFile = useCallback(() => {
    setItems([]);
    setFileName("");
  }, [setItems, setFileName]);

  const handleNextStep = useCallback(async () => {
    if (designId) {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", fileData.data);
      return axios
        .post(`${BULK_CREATE_ENDPOINT}/${designId}/bulk_create`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          checkCeleryTaskStatus(res.data.task_id, () => {})
            .then((res) => {
              setLoading(false);
              if (res.response.status === "SUCCESS") {
                window.location.reload();
              }
            })
            .catch((err) => {
              setLoading(false);
              showToast({
                description: t("errors.something_went_wrong", { ns: "common" }),
                title: "Error",
                status: "error",
              });
              return;
            });
        })
        .catch((err) => {
          setLoading(false);
          showToast({
            description: has(err?.response?.data, "file")
              ? err?.response?.data.file[0]
              : t("bulk_artboard_creation.errors.invalid_content"),
            title: "Error",
            status: "error",
          });
          return;
        });
    }
  }, [
    dispatch,
    t,
    setPreparingInProgress,
    setProgressPercent,
    fetchScraperData,
    items,
    defaultTimezone,
    workspace?.id,
    userId,
    handleOpenUpgradeModal,
  ]);

  const handleActionEditItem = useCallback(
    (action, data) => {
      if (action !== PostEditorActions.SAVE) {
        return;
      }

      const tab = Object.keys(data)[0];

      dispatch(
        updatePostsBulkImportItem({
          ...itemEdit,
          status: ImportPostStatus.READY_TO_IMPORT,
          errors: [],
          post: {
            ...data[tab],
          },
        })
      );
    },
    [dispatch, itemEdit]
  );

  const handleCloseEditItem = useCallback(() => {
    setItemEdit(null);
  }, [setItemEdit]);

  useEffect(() => {
    const onUppyUpload = ({ fileIDs }) => {
      const file = uppy.getFile(fileIDs?.[0]);
      if (file) {
        const name = file.name || "";
        setFileData(file);
        setIsUploading(true);
        const onError = (error) => {
          setItems([]);
          setFileName("");
          showToast({
            description: error,
            title: "Error",
            status: "error",
          });
        };

        file.data
          .text()
          .then((stringData) => {
            try {
              const data = parseToArray(stringData);

              if (!itemsLimit || data.length <= itemsLimit) {
                const itemsData = data.map((item) => ({
                  date: item[0] || "",
                  time: item[1] || "",
                  message: item[2] || "",
                  link: item[3] || "",
                }));

                setItems(itemsData);
                setFileName(name);
              } else {
                onError(
                  t("posts_bulk_import.errors.row_limit_exceeded", {
                    count: itemsLimit,
                  })
                );
              }
            } catch (error) {
              onError(t("posts_bulk_import.errors.parse_file"));
            }
          })
          .catch(() => {
            onError(t("posts_bulk_import.errors.parse_file"));
          })
          .finally(() => {
            setIsUploading(false);
          });
      } else {
        setItems([]);
        setFileName("");
      }
    };

    const onUppyComplete = () => {
      uppy.reset();
    };

    const onUppyInfoVisible = () => {
      const { info } = uppy.getState();
      const items = [...info];
      const lastInfo = items.pop();

      if (lastInfo?.message && ["error", "warning"].includes(lastInfo.type)) {
        showToast({
          description: lastInfo.message,
          title: "Error",
          status: "error",
        });
      }
    };

    uppy
      .on("upload", onUppyUpload)
      .on("complete", onUppyComplete)
      .on("info-visible", onUppyInfoVisible);

    return () => {
      uppy
        .off("upload", onUppyUpload)
        .off("complete", onUppyComplete)
        .off("info-visible", onUppyInfoVisible);
    };
  }, [uppy, itemsLimit]);

  useEffect(() => {
    onProgress?.(isUploading || preparingInProgress);
  }, [isUploading, preparingInProgress]);

  return (
    <>
      <Box>
        <Box paddingX="4">
          <Flex direction={isSmallScreen ? "column" : "row"} paddingY="3">
            <Box flex={isSmallScreen ? null : "1 1 55%"}>
              <Box fontSize="lg" fontWeight="500" marginBottom="2">
                {t("posts_bulk_import.how_to.title")}
              </Box>
              <Flex alignItems="flex-start" marginBottom="3">
                <Box width="8">
                  <Icon as={FaCloudDownloadAlt} color="gray.100" />
                </Box>
                <Box flex="1">
                  <Box fontSize="sm" fontWeight="500" marginBottom="1">
                    {t("posts_bulk_import.how_to.sample.title")}
                  </Box>
                  <Box fontSize="xs" color="gray.200" marginBottom="2">
                    {t("posts_bulk_import.how_to.sample.description")}
                  </Box>
                  <Button
                    isExternal
                    size="xs"
                    leftIcon={<Icon as={FaDownload} />}
                  >
                    <CSVLink
                      headers={fileHeaders}
                      data={newLayers}
                      filename="bulk-artborad-creation-example.csv"
                      target="_blank"
                      className="bulk-artborad-creation-example"
                    >
                      {t("posts_bulk_import.how_to.sample.download_file")}
                    </CSVLink>
                  </Button>
                </Box>
              </Flex>
              <Flex alignItems="flex-start" marginBottom="3">
                <Box width="8">
                  <Icon as={FaPen} color="gray.100" />
                </Box>
                <Box flex="1">
                  <Box fontSize="sm" fontWeight="500" marginBottom="1">
                    {t("posts_bulk_import.how_to.details.title")}
                  </Box>
                  <Box fontSize="xs" color="gray.200">
                    <Text marginBottom="1">
                      {t("posts_bulk_import.how_to.details.columns_title")}
                    </Text>
                    <Box paddingX="2">
                      <Text>
                        <b>A</b> -{" "}
                        {t("posts_bulk_import.how_to.details.column_a")}
                      </Text>
                      <Text>
                        <b>B</b> -{" "}
                        {t("posts_bulk_import.how_to.details.column_b")}
                      </Text>
                      <Text>
                        <b>C</b> -{" "}
                        {t("posts_bulk_import.how_to.details.column_c")}
                      </Text>
                      <Text>
                        <b>D</b> -{" "}
                        {t("posts_bulk_import.how_to.details.column_d")}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              </Flex>
              <Flex alignItems="flex-start">
                <Box width="8">
                  <Icon as={FaVideo} color="gray.100" />
                </Box>
                <Box flex="1">
                  <Box fontSize="sm" fontWeight="500" marginBottom="1">
                    {t("posts_bulk_import.how_to.tutorial.title")}
                  </Box>
                  <Box fontSize="xs" color="gray.200" marginBottom="1">
                    {t("posts_bulk_import.how_to.tutorial.description")}
                  </Box>
                  <Link
                    isExternal
                    href="https://simplified.co/academy"
                    fontSize="sm"
                  >
                    {t("posts_bulk_import.how_to.tutorial.watch_video")}{" "}
                    <Icon as={FaArrowRight} />
                  </Link>
                </Box>
              </Flex>
            </Box>
            <Flex
              flex={isSmallScreen ? null : "1 1 45%"}
              marginLeft={isSmallScreen ? null : "3"}
              marginTop={isSmallScreen ? "3" : null}
              paddingLeft={isSmallScreen ? null : "3"}
              paddingTop={isSmallScreen ? "3" : null}
              borderLeft={isSmallScreen ? null : "1px solid"}
              borderTop={isSmallScreen ? "1px solid" : null}
              borderColor="whiteAlpha.300"
              direction="column"
            >
              <Box fontSize="sm" fontWeight="500" marginBottom="4">
                {t("posts_bulk_import.settings.file.title")}
              </Box>

              {items.length && !isUploading ? (
                <Flex alignItems="center">
                  <Icon as={FaCheck} color="brand.positive-light" />
                  <Box
                    fontSize="sm"
                    color="gray.100"
                    wordBreak="break-all"
                    marginX="2"
                  >
                    {`${fileName || "file.csv"} (${items.length})`}
                  </Box>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onClick={handleResetSelectedFile}
                    isDisabled={preparingInProgress}
                    icon={<Icon as={FaTrash} fontSize="sm" />}
                  />
                </Flex>
              ) : (
                <UppyDropFile
                  uppy={uppy}
                  note={t("posts_bulk_import.upload.info", {
                    file_size: `${maxFileSize / (1024 * 1024)}MB`,
                    count: itemsLimit,
                  })}
                  isLoading={isUploading}
                />
              )}
            </Flex>
          </Flex>
        </Box>
        <Flex
          justifyContent="flex-end"
          padding="4"
          borderTop="1px solid"
          borderColor="whiteAlpha.300"
        >
          <ButtonGroup size="sm">
            <Button
              variant="solid"
              onClick={handleNextStep}
              isDisabled={isUploading || !items.length || loading}
              isLoading={preparingInProgress || loading}
              loadingText={
                preparingInProgress
                  ? `${progressPercent}%`
                  : t("next", { ns: "common" })
              }
              rightIcon={<Icon as={FaArrowRight} />}
            >
              {t("next", { ns: "common" })}
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
      {itemEdit ? (
        <PostEditorViewModal
          onClose={handleCloseEditItem}
          onActionDone={handleCloseEditItem}
          defaultPostData={itemEdit.post}
          mode={PostEditorModes.CUSTOM_EDIT}
          onCustomAction={handleActionEditItem}
          customHideTimeSelector
        />
      ) : null}
    </>
  );
};

BulkArtboardCreation.propTypes = {
  onClose: PropTypes.func,
};

BulkArtboardCreation.defaultProps = {
  onClose: undefined,
};

export default BulkArtboardCreation;
